import { Component, ViewChild, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform, base64ToFile } from 'ngx-image-cropper';
import { blobToFile } from 'src/app/app.functions';

@Component({
    selector: 'app-image-cropper',
    templateUrl: 'image-cropper.template.html',
    inputs: ['showCropperDialog', 'isAdditionalImage', 'image'],
    outputs: ['onImageCropped', 'onImageCropperClose'],
    standalone: false
})
export class AppImageCropperComponent implements OnInit, OnDestroy {
  
  @ViewChild(ImageCropperComponent) imageCropper!: ImageCropperComponent;
  public showCropperDialog: boolean = false;
  public isAdditionalImage?: boolean;
  public image?: File | null;
  public onImageCropped = new EventEmitter<ImageCroppedResponse>();
  public onImageCropperClose = new EventEmitter<any>();
  public showCropper: boolean = false;
  public selectedFileBase64String: any;
  public canvasRotation = 0;
  public rotation?: number = 0;
  public scale = 1;
  public containWithinAspectRatio = false;
  public transform: ImageTransform = {};

  public ngOnInit() {
    if (this.image) {
      //as soon as this component gets loaded, we'll load this input image as a Base64 string.
      //the reason for doing it is when this file is coverted Base64, the resulting string is bound to the image cropper
      //which will be called as soon as its input value is changed and the cropper will be shown on the UI
      this.loadBase64FromFile(this.image);
    }
  }

  private loadBase64FromFile(file: File) {
    var reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      //setting the base64 of the current selected file will result in image cropper being shown 
      //in the cropper dialog (if we're dealing with an image obviously)
      this.selectedFileBase64String = reader.result;
    };
    reader.onerror = (e: any) => { console.error(e); };
  }

  public onCropImageDialogClose($event: any) {
    //user has closed the dialog so reset the image before hiding it
    console.log("onCropImageDialogClose triggered",$event); // Debugging log
    this.resetImage();
    this.showCropperDialog = false;
    this.onImageCropperClose.emit({
      skipCropping: true
    });
  }

  public imageLoaded($event: any) {
    //image has been loaded so show cropper
    this.showCropper = true;
  }

  public cropperReady() {
    //cropper ready
  }

  //this will get called when the user presses the crop button
  public cropImage($event: any) {
    if (this.imageCropper) {
      //calling the crop method on the image cropper component object will result in image being cropped at the current position and also
      //results in firing of the output event "(imageCropped)" of the cropper.
      this.imageCropper.crop();
    }
  }

  public imageCropped($event: ImageCroppedEvent) {
    //console.log($event);
    var blob: Blob | null | undefined = $event.blob;
    if (blob && this.image) {
      var croppedFile: File = blobToFile(blob, this.image.name, this.image.lastModified);
      //emit the cropped image to the parent component
      this.onImageCropped.emit({
        CroppedImage: croppedFile,
        IsAdditionalImage: this.isAdditionalImage,
      });
    }
  }

  public loadImageFailed() {
    //image couldn't load properly so show a message
  }

  public rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  public rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }


  public flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH
    };
  }

  public flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV
    };
  }

  public resetImage() {
    this.scale = 1;
    this.rotation = 0;
    this.canvasRotation = 0;
    this.transform = {};
  }

  public zoomOut() {
    this.scale -= .1;
    this.transform = {
      ...this.transform,
      scale: this.scale
    };
  }

  public zoomIn() {
    this.scale += .1;
    this.transform = {
      ...this.transform,
      scale: this.scale
    };
  }

  public toggleContainWithinAspectRatio() {
    this.containWithinAspectRatio = !this.containWithinAspectRatio;
  }

  public updateRotation() {
    this.transform = {
      ...this.transform,
      rotate: this.rotation
    };
  }

  public ngOnDestroy() {
    this.showCropperDialog = false;
  }
}

export class ImageCroppedResponse {
  CroppedImage!: File;
  IsAdditionalImage?: boolean;
}